import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Calendar,
  Clock,
  ChevronRight,
  FileText,
  Target,
  TrendingUp,
  Award,
  BookOpen,
  Briefcase,
  CheckCircle,
  Eye,
  History,
  Loader2,
  MessageSquare,
  Sparkles,
  User,
  X
} from "lucide-react";

const AssessmentHistory = () => {
  const { token } = useContext(AuthContext);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchAssessmentHistory();
  }, [token]);

  const fetchAssessmentHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/assessments/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessments(response.data || []);
    } catch (error) {
      console.error("Error fetching assessment history:", error);
      toast.error("Failed to load assessment history");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessmentDetail = async (resultId) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(`${API}/assessments/results/${resultId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAssessment(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Error fetching assessment detail:", error);
      toast.error("Failed to load assessment details");
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const calculateTotalScore = (score) => {
    if (!score || typeof score !== 'object') return 0;
    const values = Object.values(score);
    if (values.length === 0) return 0;
    return Math.round((values.reduce((a, b) => a + b, 0) / (values.length * 3)) * 100);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600 bg-green-100";
    if (score >= 60) return "text-blue-600 bg-blue-100";
    if (score >= 40) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  const getTopCategories = (score) => {
    if (!score || typeof score !== 'object') return [];
    return Object.entries(score)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category);
  };

  // Detail Modal Component
  const AssessmentDetailModal = () => {
    if (!selectedAssessment) return null;

    return (
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  {selectedAssessment.assessment_name || "Career Assessment"}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    {formatDate(selectedAssessment.created_at)}
                  </span>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {selectedAssessment.status || "Completed"}
                  </Badge>
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(90vh-120px)]">
            <div className="p-6 space-y-6">
              {/* Score Summary */}
              {selectedAssessment.score && Object.keys(selectedAssessment.score).length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-indigo-600" />
                      Category Scores
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(selectedAssessment.score).map(([category, score]) => (
                        <div key={category} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 capitalize mb-1">{category}</p>
                          <div className="flex items-center gap-2">
                            <Progress value={Math.min(score * 33, 100)} className="flex-1 h-2" />
                            <span className="text-sm font-bold text-indigo-600">{score}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pre-Assessment Context */}
              {selectedAssessment.pre_assessment_data && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-600" />
                      Your Profile Context
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedAssessment.pre_assessment_data.interests && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Interests</p>
                          <p className="text-sm text-gray-600">
                            {Array.isArray(selectedAssessment.pre_assessment_data.interests) 
                              ? selectedAssessment.pre_assessment_data.interests.join(", ")
                              : selectedAssessment.pre_assessment_data.interests}
                          </p>
                        </div>
                      )}
                      {selectedAssessment.pre_assessment_data.ambitions && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Ambitions</p>
                          <p className="text-sm text-gray-600">{selectedAssessment.pre_assessment_data.ambitions}</p>
                        </div>
                      )}
                      {selectedAssessment.pre_assessment_data.preferred_field && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Preferred Field</p>
                          <p className="text-sm text-gray-600">{selectedAssessment.pre_assessment_data.preferred_field}</p>
                        </div>
                      )}
                      {selectedAssessment.pre_assessment_data.favorite_subjects && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Favorite Subjects</p>
                          <p className="text-sm text-gray-600">
                            {Array.isArray(selectedAssessment.pre_assessment_data.favorite_subjects) 
                              ? selectedAssessment.pre_assessment_data.favorite_subjects.join(", ")
                              : selectedAssessment.pre_assessment_data.favorite_subjects}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Questions & Answers */}
              {selectedAssessment.questions && selectedAssessment.questions.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      Questions & Your Answers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedAssessment.questions.map((question, idx) => {
                        const answer = selectedAssessment.answers?.[question.id];
                        const selectedAnswer = answer?.selected_answer || answer;
                        const customAnswer = answer?.custom_answer;
                        
                        return (
                          <div key={question.id || idx} className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-7 h-7 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-bold">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                <p className="font-medium text-gray-800 mb-2">{question.question}</p>
                                {question.category && (
                                  <Badge variant="outline" className="text-xs mb-2">
                                    {question.category}
                                  </Badge>
                                )}
                                
                                {/* Selected Answer */}
                                {selectedAnswer && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">Selected Answer:</p>
                                    <p className="text-sm text-indigo-700 bg-indigo-50 px-3 py-2 rounded-md">
                                      {Array.isArray(selectedAnswer) ? selectedAnswer.join(", ") : selectedAnswer}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Custom Answer */}
                                {customAnswer && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                      <Sparkles className="w-3 h-3" />
                                      Custom Response:
                                    </p>
                                    <p className="text-sm text-purple-700 bg-purple-50 px-3 py-2 rounded-md italic">
                                      "{customAnswer}"
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Career Recommendations */}
              {selectedAssessment.detailed_recommendations && selectedAssessment.detailed_recommendations.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-green-600" />
                      Career Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {selectedAssessment.detailed_recommendations.map((rec, idx) => (
                        <div key={idx} className="border-l-4 border-indigo-500 pl-4 py-2">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-lg font-bold text-gray-800">{idx + 1}. {rec.career_name}</h4>
                            {rec.salary_range && (
                              <Badge variant="outline" className="text-green-700 bg-green-50">
                                {rec.salary_range}
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-3">{rec.why_good_fit}</p>
                          
                          {rec.skills_to_develop && rec.skills_to_develop.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-700 mb-1">Skills to Develop:</p>
                              <div className="flex flex-wrap gap-1">
                                {rec.skills_to_develop.map((skill, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {skill}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {rec.what_to_study && rec.what_to_study.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-700 mb-1">What to Study:</p>
                              <ul className="text-xs text-gray-600 list-disc list-inside">
                                {rec.what_to_study.slice(0, 3).map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {rec.job_outlook && (
                            <p className="text-xs text-blue-600 mt-2">
                              <TrendingUp className="w-3 h-3 inline mr-1" />
                              {rec.job_outlook}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Legacy Recommendations (if no detailed ones) */}
              {(!selectedAssessment.detailed_recommendations || selectedAssessment.detailed_recommendations.length === 0) && 
               selectedAssessment.recommendations && selectedAssessment.recommendations.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-orange-600" />
                      Key Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {selectedAssessment.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <History className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Assessment History</h3>
          <p className="text-gray-600 mb-4">
            You haven't completed any assessments yet. Start your first assessment to get personalized career recommendations.
          </p>
          <Button onClick={() => window.location.reload()}>
            <Target className="w-4 h-4 mr-2" />
            Take Your First Assessment
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* History Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" />
            Assessment History
          </h2>
          <p className="text-sm text-gray-600">
            {assessments.length} assessment{assessments.length !== 1 ? 's' : ''} completed
          </p>
        </div>
      </div>

      {/* Assessment Cards */}
      <div className="grid gap-4">
        {assessments.map((assessment, idx) => {
          const totalScore = calculateTotalScore(assessment.score);
          const topCategories = getTopCategories(assessment.score);
          const careerCount = assessment.detailed_recommendations?.length || 
                             assessment.recommendations?.length || 0;

          return (
            <Card 
              key={assessment.id || idx} 
              className="hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => fetchAssessmentDetail(assessment.id)}
              data-testid={`assessment-history-card-${idx}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center shadow-sm">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {assessment.assessment_name || "Career Assessment"}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {formatDate(assessment.created_at)}
                          <span className="text-gray-300">•</span>
                          <span className="text-indigo-600">{getRelativeTime(assessment.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Score Badge */}
                      {assessment.score && Object.keys(assessment.score).length > 0 && (
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(totalScore)}`}>
                          Score: {totalScore}%
                        </div>
                      )}

                      {/* Top Categories */}
                      {topCategories.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Top:</span>
                          {topCategories.map((cat, i) => (
                            <Badge key={i} variant="outline" className="text-xs capitalize">
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Career Recommendations Count */}
                      {careerCount > 0 && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Briefcase className="w-3 h-3" />
                          {careerCount} career path{careerCount !== 1 ? 's' : ''}
                        </div>
                      )}

                      {/* Status */}
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {assessment.status || "Completed"}
                      </Badge>
                    </div>

                    {/* Quick Preview of Recommendations */}
                    {assessment.detailed_recommendations && assessment.detailed_recommendations.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-500 mb-2">Recommended Careers:</p>
                        <div className="flex flex-wrap gap-2">
                          {assessment.detailed_recommendations.slice(0, 3).map((rec, i) => (
                            <Badge key={i} className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200">
                              {rec.career_name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* View Details Arrow */}
                  <div className="flex items-center">
                    {detailLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    ) : (
                      <Button variant="ghost" size="sm" className="group-hover:bg-indigo-50">
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail Modal */}
      <AssessmentDetailModal />
    </div>
  );
};

export default AssessmentHistory;
